import { PDFDocument } from "pdf-lib";

/**
 * Render a print-ready HTML document (see `buildNotePrintHtml`) to letter-size
 * PDF bytes, entirely in the browser.
 *
 * Why rasterize rather than lay the document out again in @react-pdf/renderer,
 * the way the fax cover sheet and requisitions do: a printed note is ~1,200
 * lines of HTML built from the note's answers, the patient's demographics,
 * medications, billing codes, photos, and the signing provider's signature —
 * rebuilding that in a second renderer would mean two layouts that drift apart,
 * and a faxed note that doesn't match the one the same dialog prints. The fax
 * itself is transmitted as a ~200 dpi bitmap, so an image-backed PDF loses
 * nothing on the wire.
 *
 * The document is laid out in a hidden, letter-width iframe and photographed a
 * page at a time. Page breaks are pulled up above any block that would
 * otherwise be sliced through the middle.
 */

/** 8.5in at 96 CSS px/in — the width the print stylesheet lays out against. */
const PAGE_WIDTH_PX = 816;
/** Letter, in PDF points. */
const PAGE_WIDTH_PT = 612;
const PAGE_HEIGHT_PT = 792;
/** CSS px -> pt. */
const PX_TO_PT = PAGE_WIDTH_PT / PAGE_WIDTH_PX;
/**
 * Breathing room at the top and bottom of every page. The document's own 0.5in
 * body padding covers the first and last page; this keeps the interior page
 * breaks clear of the edge, which fax machines crop.
 */
const PAGE_MARGIN_PT = 24;
const PAGE_CONTENT_PX = Math.floor(
  (PAGE_HEIGHT_PT - PAGE_MARGIN_PT * 2) / PX_TO_PT,
);
/**
 * A break is only worth pulling up if it doesn't strand most of a page. Below
 * this, the straddling block is sliced instead.
 */
const MIN_PAGE_FILL = 0.55;
/** Guard against a runaway document eating the tab's memory. */
const MAX_PAGES = 60;
/** The upload endpoint rejects anything larger (10MB); stay under it. */
const MAX_PDF_BYTES = 9 * 1024 * 1024;
/**
 * Quality ladder, walked down only if the PDF comes out too large for the
 * upload. The first entry is what a normal note renders at: 2x a 96dpi letter
 * page is ~192 dpi, just over fax's 204x196 "fine" resolution.
 */
const QUALITY_STEPS: { pixelRatio: number; quality: number }[] = [
  { pixelRatio: 2, quality: 0.82 },
  { pixelRatio: 1.5, quality: 0.72 },
  { pixelRatio: 1, quality: 0.6 },
];

interface PageSegment {
  top: number;
  height: number;
}

/** Resolve once every image in the document has loaded (or failed). */
async function waitForImages(doc: Document): Promise<void> {
  const images = Array.from(doc.images);
  await Promise.all(
    images.map(async (img) => {
      if (img.complete) return;
      try {
        await img.decode();
      } catch {
        // A broken image prints as a broken image; it must not block the fax.
      }
    }),
  );
}

/**
 * Boxes that should not be sliced through: the leaves of the document (a
 * paragraph, a table row, a photo) plus anything the print stylesheet marks
 * `break-inside: avoid` (medication rows, photo figures, the code grids).
 */
function collectAtomicBoxes(root: HTMLElement): PageSegment[] {
  const rootTop = root.getBoundingClientRect().top;
  const boxes: PageSegment[] = [];

  for (const el of Array.from(root.querySelectorAll<HTMLElement>("*"))) {
    const style = el.ownerDocument.defaultView?.getComputedStyle(el);
    const isAtomic =
      el.childElementCount === 0 ||
      style?.breakInside === "avoid" ||
      style?.pageBreakInside === "avoid";
    if (!isAtomic) continue;

    const rect = el.getBoundingClientRect();
    if (rect.height <= 0) continue;
    boxes.push({ top: rect.top - rootTop, height: rect.height });
  }

  return boxes;
}

/**
 * Split a column of content `totalHeight` tall into page-sized segments,
 * moving each break up above any atomic box it would cut through.
 */
export function planPageSegments(
  totalHeight: number,
  boxes: PageSegment[],
  pageHeight: number = PAGE_CONTENT_PX,
): PageSegment[] {
  const segments: PageSegment[] = [];
  let top = 0;

  while (top < totalHeight && segments.length < MAX_PAGES) {
    if (totalHeight - top <= pageHeight) {
      segments.push({ top, height: totalHeight - top });
      break;
    }

    const limit = top + pageHeight;
    let breakAt = limit;
    // One straddling box can be pushed onto the next page only to have another
    // straddle the new break, so the scan repeats until it settles.
    for (let pass = 0; pass < 4; pass++) {
      let moved = breakAt;
      for (const box of boxes) {
        const bottom = box.top + box.height;
        const straddles = box.top < moved && bottom > moved;
        // A box taller than a page can never be placed whole; slice it.
        if (straddles && box.height <= pageHeight && box.top > top) {
          moved = Math.min(moved, box.top);
        }
      }
      if (moved === breakAt) break;
      breakAt = moved;
    }

    // Refusing to strand half a page: fall back to the hard break and let the
    // offending block be sliced.
    if (breakAt - top < pageHeight * MIN_PAGE_FILL) breakAt = limit;

    segments.push({ top, height: breakAt - top });
    top = breakAt;
  }

  return segments;
}

/** Mount the document off-screen at letter width, ready to be photographed. */
async function mountPrintDocument(html: string): Promise<{
  iframe: HTMLIFrameElement;
  body: HTMLElement;
  content: HTMLElement;
}> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = `${PAGE_WIDTH_PX}px`;
  iframe.style.height = "1056px";
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    iframe.remove();
    throw new Error("Unable to prepare the document for faxing.");
  }

  doc.open();
  doc.write(html);
  doc.close();

  // The printed document lays itself out on <body> (0.5in padding, 8.5in max
  // width, a `min-height: 100vh` flex column). Faxing needs to clip <body> to
  // one page at a time, and clipping a flex container squashes its children —
  // so the content moves into a wrapper that inherits the same box, leaving
  // <body> as a plain window onto it.
  const body = doc.body;
  const content = doc.createElement("div");
  content.style.cssText = [
    "box-sizing: border-box",
    `width: ${PAGE_WIDTH_PX}px`,
    "padding: 0.5in",
    "display: flex",
    "flex-direction: column",
    "transform-origin: top left",
  ].join(";");
  while (body.firstChild) content.appendChild(body.firstChild);
  body.appendChild(content);
  body.style.cssText = [
    "margin: 0",
    "padding: 0",
    "max-width: none",
    "min-height: 0",
    "display: block",
    "overflow: hidden",
    "background: #ffffff",
    `width: ${PAGE_WIDTH_PX}px`,
  ].join(";");

  await waitForImages(doc);
  if (doc.fonts?.ready) {
    try {
      await doc.fonts.ready;
    } catch {
      // System fonts only; a rejected font promise is not worth failing over.
    }
  }

  return { iframe, body, content };
}

export interface RenderPrintHtmlToPdfResult {
  blob: Blob;
  pageCount: number;
}

/**
 * Render `html` to a letter-size PDF. Throws when the document cannot be laid
 * out or comes out too large to upload even at the lowest quality step.
 */
export async function renderPrintHtmlToPdf(
  html: string,
): Promise<RenderPrintHtmlToPdfResult> {
  // Loaded lazily: html-to-image is only ever needed on a fax, and it should
  // not sit in the bundle of every page that can print a note.
  const { toJpeg } = await import("html-to-image");
  const { iframe, body, content } = await mountPrintDocument(html);

  try {
    const totalHeight = content.getBoundingClientRect().height;
    if (totalHeight <= 0) {
      throw new Error("The note came out empty and can't be faxed.");
    }

    const segments = planPageSegments(totalHeight, collectAtomicBoxes(content));

    for (let index = 0; index < QUALITY_STEPS.length; index++) {
      const step = QUALITY_STEPS[index];
      const pdfDoc = await PDFDocument.create();

      for (const segment of segments) {
        body.style.height = `${segment.height}px`;
        content.style.transform = `translateY(-${segment.top}px)`;

        const dataUrl = await toJpeg(body, {
          width: PAGE_WIDTH_PX,
          height: segment.height,
          pixelRatio: step.pixelRatio,
          quality: step.quality,
          backgroundColor: "#ffffff",
          // Only system fonts are used, and embedding walks stylesheets the
          // document doesn't have.
          skipFonts: true,
        });

        const image = await pdfDoc.embedJpg(dataUrl);
        const page = pdfDoc.addPage([PAGE_WIDTH_PT, PAGE_HEIGHT_PT]);
        const drawHeight = segment.height * PX_TO_PT;
        page.drawImage(image, {
          x: 0,
          y: PAGE_HEIGHT_PT - PAGE_MARGIN_PT - drawHeight,
          width: PAGE_WIDTH_PT,
          height: drawHeight,
        });
      }

      const bytes = await pdfDoc.save();
      if (bytes.byteLength <= MAX_PDF_BYTES) {
        return {
          // `bytes` is a Uint8Array view; copy through its buffer slice so the
          // Blob owns exactly the PDF and nothing around it.
          blob: new Blob([bytes.slice().buffer], { type: "application/pdf" }),
          pageCount: segments.length,
        };
      }

      if (index === QUALITY_STEPS.length - 1) {
        throw new Error(
          "This note is too large to fax. Try again without the photos.",
        );
      }
    }

    // Unreachable: the loop either returns or throws on its last step.
    throw new Error("Unable to prepare the note for faxing.");
  } finally {
    iframe.remove();
  }
}
